import React from "react";
import FormCheckbox from "src/shared/components/FormCheckbox";
import CategorySelect from "./CategorySelect";
import ImageUploader from "../../ImageUploader";
import {
  Category,
  StaffMember,
  ProductFormData,
} from "src/shared/services/interfacev2";
import { formatAmount, removeEmojis } from "../utils/formatters";
import StaffSelect from "./StaffSelect";
import InputGroup from "src/business-types/grocery/layouts/setting/customer/InputGroup";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import { useUser } from "src/shared/context/UserContext";
import { normalizeFoodTypes } from "src/shared/utils/normalizeFoodTypes";

interface ProductFormProps {
  formData: ProductFormData;
  categories: Category[];
  staffMembers: StaffMember[];
  businessType: string;
  onChange: (field: keyof ProductFormData, value: any) => void;
  showErrors?: boolean;
  previewImage?: string | null;
}

const ProductForm: React.FC<ProductFormProps> = ({
  formData,
  categories,
  staffMembers,
  businessType,
  onChange,
  showErrors = false,
  previewImage,
}) => {
  const { userConfigration } = useUser();
  const handleProductNameChange = (value: string) => {
    onChange("productName", removeEmojis(value));
  };

  // Normalize food types from user configuration
  const foodTypes = userConfigration?.foodTypes
    ? normalizeFoodTypes(userConfigration.foodTypes)
    : ["Veg", "Non-Veg"];

  return (
    <div className=" space-y-3">
      {/* Top Section */}
      <div className="grid grid-cols-[1fr_140px] gap-4 items-start">
        {/* Left Inputs */}
        <div className="space-y-2">
          {/* Product Name */}
          <InputGroup
            label="Product name"
            name="productName"
            type="text"
            mandatory
            value={formData.productName}
            onChange={(_, value) => handleProductNameChange(value)}
          />

          {/* Arabic Name */}
          <InputGroup
            label="Arabic name"
            name="arabicName"
            type="text"
            mandatory
            value={formData.arabicName}
            onChange={(_, value) => onChange("arabicName", removeEmojis(value))}
          />
        </div>

        {/* Right Image */}
        <div className="flex justify-end">
          <ImageUploader
            value={formData.image ?? null}
            previewImage={previewImage}
            onChange={(file) => onChange("image", file)}
            maxSizeMB={5}
          />
        </div>
      </div>
      <InputGroup
        label="Product price"
        name="amount"
        type="number"
        mandatory
        value={formData.amount}
        onChange={(_, value) => {
          const formatted = formatAmount(value);
          onChange("amount", formatted);
        }}
        error={formData.amount === "0" || parseFloat(formData.amount) < 0}
        helperText={
          formData.amount === "0"
            ? "Price must be greater than 0"
            : parseFloat(formData.amount) < 0
              ? "Price cannot be negative"
              : ""
        }
      />

      {/* Remaining Fields */}
      {businessType === "Restaurant" && (
        <CustomDropdownSelect
          name="type"
          title="Type"
          value={formData.type ?? ""}
          mandatory
          options={foodTypes.map((type: string) => ({
            _id: type,
            CategoryName: type,
          }))}
          titleofSelect="Select type"
          onChange={(value) => onChange("type", value)}
        />
      )}

      <CategorySelect
        categories={categories}
        value={formData.categoryId}
        onChange={(v) => onChange("categoryId", v)}
        required
        showError={showErrors}
      />

      {businessType === "Saloon" && (
        <StaffSelect
          staffMembers={staffMembers}
          value={formData.staffList}
          onChange={(v) => onChange("staffList", v)}
        />
      )}

      <FormCheckbox
        label="Its Inward Product"
        checked={formData.ownProduct}
        onChange={(_, checked) => onChange("ownProduct", checked)}
      />
    </div>
  );
};

export default ProductForm;
